<template>
  <div class="documentary-submission">
    <button class="close-btn" @click="$emit('close')">
      <i class="fas fa-times"></i>
    </button>
    
    <div class="submission-header">
      <h2>纪录片收录</h2>
      <p>帮助我们发现更多优秀的纪录片作品</p>
    </div>

    <form @submit.prevent="handleSubmit" class="submission-form">
      <div class="form-section">
        <h3>基本信息</h3>
        <div class="form-grid">
          <div class="form-group">
            <label for="title">片名 *</label>
            <input
              id="title"
              v-model="form.title"
              type="text"
              required
              placeholder="请输入纪录片片名"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="director">导演 *</label>
            <input
              id="director"
              v-model="form.director"
              type="text"
              required
              placeholder="请输入导演姓名"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="year">年份 *</label>
            <input
              id="year"
              v-model="form.year"
              type="number"
              required
              min="1900"
              :max="new Date().getFullYear()"
              :placeholder="String(new Date().getFullYear())"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="duration">时长（分钟）</label>
            <input
              id="duration"
              v-model="form.duration"
              type="number"
              min="1"
              placeholder="如：90"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="tvChannel">电视频道 *</label>
            <div class="custom-select-wrapper">
              <input
                id="tvChannel"
                v-model="tvChannelSearch"
                type="text"
                placeholder="请选择电视频道"
                class="form-input"
                @focus="showTvChannelDropdown = true; filterTvChannels()"
                @input="onTvChannelInput"
                @blur="onTvChannelBlur"
                required
                autocomplete="off"
              />
              <div v-if="showTvChannelDropdown && filteredTvChannels.length > 0" class="custom-select-dropdown">
                <div
                  v-for="channel in filteredTvChannels"
                  :key="channel.id"
                  class="custom-select-option"
                  @mousedown.prevent="selectTvChannel(channel)"
                >
                  {{ channel.name }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label for="category">分类</label>
            <div class="custom-select-wrapper">
              <input
                id="category"
                v-model="categorySearch"
                type="text"
                placeholder="请选择分类"
                class="form-input"
                @focus="showCategoryDropdown = true; filterCategories()"
                @input="onCategoryInput"
                @blur="onCategoryBlur"
                autocomplete="off"
              />
              <div v-if="showCategoryDropdown && filteredCategories.length > 0" class="custom-select-dropdown">
                <div
                  v-for="category in filteredCategories"
                  :key="category.id"
                  class="custom-select-option"
                  @mousedown.prevent="selectCategory(category)"
                >
                  {{ category.name }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label for="platform">播放平台</label>
            <div class="custom-select-wrapper">
              <input
                id="platform"
                v-model="platformSearch"
                type="text"
                placeholder="请选择播放平台"
                class="form-input"
                @focus="showPlatformDropdown = true; filterPlatforms()"
                @input="onPlatformInput"
                @blur="onPlatformBlur"
                autocomplete="off"
              />
              <div v-if="showPlatformDropdown && filteredPlatforms.length > 0" class="custom-select-dropdown">
                <div
                  v-for="platform in filteredPlatforms"
                  :key="platform.platform_id"
                  class="custom-select-option"
                  @mousedown.prevent="selectPlatform(platform)"
                >
                  {{ platform.name }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label for="videoUrl">视频链接</label>
            <input
              id="videoUrl"
              v-model="form.videoUrl"
              type="text"
              placeholder="请输入视频播放链接（如 https://...）"
              class="form-input"
              :class="{ 'input-error': videoUrlError }"
              @blur="validateVideoUrl"
            />
            <p v-if="videoUrlError" class="field-error">{{ videoUrlError }}</p>
          </div>

          <div class="form-group">
            <label for="country">制片国家/地区</label>
            <div class="custom-select-wrapper">
              <input
                id="country"
                v-model="countrySearch"
                type="text"
                placeholder="请选择国家/地区"
                class="form-input"
                @focus="showCountryDropdown = true; filterCountries()"
                @input="onCountryInput"
                @blur="onCountryBlur"
                autocomplete="off"
              />
              <div v-if="showCountryDropdown && filteredCountries.length > 0" class="custom-select-dropdown">
                <div
                  v-for="country in filteredCountries"
                  :key="country"
                  class="custom-select-option"
                  @mousedown.prevent="selectCountry(country)"
                >
                  {{ country }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3>详细描述</h3>
        <div class="form-group">
          <label for="description">简介 *</label>
          <textarea
            id="description"
            v-model="form.description"
            required
            rows="4"
            placeholder="请简要描述这部纪录片的内容和特色..."
            class="form-textarea"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="tags">标签</label>
          <input
            id="tags"
            v-model="tagInput"
            type="text"
            placeholder="输入标签后按回车添加"
            class="form-input"
            @keydown.enter.prevent="addTag"
          />
          <div class="tags-container">
            <span
              v-for="(tag, index) in form.tags"
              :key="index"
              class="tag"
            >
              {{ tag }}
              <button type="button" @click="removeTag(index)" class="tag-remove">
                ×
              </button>
            </span>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3>联系方式</h3>
        <div class="form-group">
          <label for="submitterName">您的姓名</label>
          <input
            id="submitterName"
            v-model="form.submitterName"
            type="text"
            placeholder="请输入您的姓名"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="submitterEmail">联系邮箱 *</label>
          <input
            id="submitterEmail"
            v-model="form.submitterEmail"
            type="email"
            required
            placeholder="请输入您的邮箱地址"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="submitReason">推荐理由</label>
          <textarea
            id="submitReason"
            v-model="form.submitReason"
            rows="3"
            placeholder="请说明您推荐这部纪录片的理由..."
            class="form-textarea"
          ></textarea>
        </div>
      </div>

      <div class="form-actions">
        <button type="button" @click="resetForm" class="btn btn-secondary">
          重置表单
        </button>
        <button type="submit" :disabled="isSubmitting" class="btn btn-primary">
          <i v-if="isSubmitting" class="fas fa-spinner fa-spin"></i>
          {{ isSubmitting ? '提交中...' : '提交收录' }}
        </button>
      </div>
    </form>

    <!-- 成功提示 -->
    <div v-if="showSuccess" class="success-modal">
      <div class="modal-content">
        <div class="success-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <h3>提交成功！</h3>
        <p>感谢您的推荐，我们会尽快审核您提交的纪录片信息。</p>
        <button @click="closeSuccess" class="btn btn-primary">
          确定
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { submissionAPI } from '@/services/api'
import { getTVChannels } from '@/api/TVChannels'
import { getCategories } from '@/api/categories'
import { getPlatforms } from '@/api/platforms'
import { useUserStore } from '@/stores/user'
import { debugError } from '@/utils/env'

// 定义emit
const emit = defineEmits<{
  close: []
}>()

const userStore = useUserStore()

interface ChannelOption {
  id: number
  name: string
  code: string
}

interface CategoryOption {
  id: number
  name: string
  slug: string
}

interface PlatformOption {
  platform_id: number
  name: string
  code: string
}

interface DocumentaryForm {
  title: string
  director: string
  year: string
  duration: string
  tvChannelId: number | null
  categoryId: number | null
  platformId: number | null
  videoUrl: string
  country: string
  description: string
  tags: string[]
  submitterName: string
  submitterEmail: string
  submitReason: string
}

const form = reactive<DocumentaryForm>({
  title: '',
  director: '',
  year: String(new Date().getFullYear()),
  duration: '',
  tvChannelId: null,
  categoryId: null,
  platformId: null,
  videoUrl: '',
  country: '',
  description: '',
  tags: [],
  submitterName: '',
  submitterEmail: '',
  submitReason: ''
})

const tagInput = ref('')
const countrySearch = ref('')
const tvChannelSearch = ref('')
const categorySearch = ref('')
const platformSearch = ref('')
const showCountryDropdown = ref(false)
const showTvChannelDropdown = ref(false)
const showCategoryDropdown = ref(false)
const showPlatformDropdown = ref(false)
const isSubmitting = ref(false)
const showSuccess = ref(false)
const videoUrlError = ref('')

// 频道、分类、平台列表（从API获取）
const tvChannels = ref<ChannelOption[]>([])
const categories = ref<CategoryOption[]>([])
const platforms = ref<PlatformOption[]>([])

// 国家/地区列表
const countries = [
  '中国', '美国', '英国', '日本', '韩国', '法国', '德国', '意大利', '西班牙', '加拿大',
  '澳大利亚', '印度', '俄罗斯', '巴西', '墨西哥', '阿根廷', '荷兰', '瑞典', '挪威', '丹麦',
  '芬兰', '瑞士', '奥地利', '比利时', '波兰', '捷克', '匈牙利', '罗马尼亚', '保加利亚', '希腊',
  '土耳其', '以色列', '阿联酋', '沙特阿拉伯', '伊朗', '埃及', '南非', '肯尼亚', '尼日利亚', '摩洛哥',
  '泰国', '越南', '菲律宾', '马来西亚', '新加坡', '印度尼西亚', '新西兰', '爱尔兰', '葡萄牙', '乌克兰'
]

const filteredCountries = ref(countries)
const filteredTvChannels = ref<ChannelOption[]>([])
const filteredCategories = ref<CategoryOption[]>([])
const filteredPlatforms = ref<PlatformOption[]>([])

// 加载频道、分类、平台数据
onMounted(async () => {
  try {
    // 自动填入已验证用户邮箱
    if (userStore.user?.email && userStore.user?.is_email_verified) {
      form.submitterEmail = userStore.user.email
    }

    // 加载频道列表
    const channelsData = await getTVChannels()
    tvChannels.value = channelsData.map(c => ({ id: c.id, name: c.name, code: c.code }))
    filteredTvChannels.value = tvChannels.value

    // 加载分类列表
    const categoriesData = await getCategories()
    categories.value = categoriesData.map(c => ({ id: c.id, name: c.name, slug: c.slug }))
    filteredCategories.value = categories.value

    // 加载平台列表
    const platformsData = await getPlatforms()
    platforms.value = platformsData.map(p => ({ platform_id: p.platform_id, name: p.name, code: p.code }))
    filteredPlatforms.value = platforms.value
  } catch (error) {
    debugError('加载数据失败:', error)
  }
})

const addTag = () => {
  const tag = tagInput.value.trim()
  if (tag && !form.tags.includes(tag)) {
    form.tags.push(tag)
    tagInput.value = ''
  }
}

const removeTag = (index: number) => {
  form.tags.splice(index, 1)
}

// ========== 电视频道：只能下拉选择 ==========
const filterTvChannels = () => {
  const searchTerm = tvChannelSearch.value.toLowerCase()
  filteredTvChannels.value = tvChannels.value.filter(channel => 
    channel.name.toLowerCase().includes(searchTerm)
  )
}

const onTvChannelInput = () => {
  // 允许输入以筛选，但不允许提交无效值
  filterTvChannels()
  showTvChannelDropdown.value = true
  // 如果输入值不匹配已选频道，清除选择
  const matched = tvChannels.value.find(c => c.name === tvChannelSearch.value)
  if (matched) {
    form.tvChannelId = matched.id
  } else {
    form.tvChannelId = null
  }
}

const onTvChannelBlur = () => {
  setTimeout(() => {
    // 验证输入值是否有效
    const matched = tvChannels.value.find(c => c.name === tvChannelSearch.value)
    if (!matched) {
      // 无效输入，清除
      tvChannelSearch.value = ''
      form.tvChannelId = null
    }
    showTvChannelDropdown.value = false
  }, 200)
}

const selectTvChannel = (channel: ChannelOption) => {
  form.tvChannelId = channel.id
  tvChannelSearch.value = channel.name
  showTvChannelDropdown.value = false
}

// ========== 分类：只能下拉选择 ==========
const filterCategories = () => {
  const searchTerm = categorySearch.value.toLowerCase()
  filteredCategories.value = categories.value.filter(category =>
    category.name.toLowerCase().includes(searchTerm)
  )
}

const onCategoryInput = () => {
  filterCategories()
  showCategoryDropdown.value = true
  const matched = categories.value.find(c => c.name === categorySearch.value)
  if (matched) {
    form.categoryId = matched.id
  } else {
    form.categoryId = null
  }
}

const onCategoryBlur = () => {
  setTimeout(() => {
    const matched = categories.value.find(c => c.name === categorySearch.value)
    if (!matched) {
      categorySearch.value = ''
      form.categoryId = null
    }
    showCategoryDropdown.value = false
  }, 200)
}

const selectCategory = (category: CategoryOption) => {
  form.categoryId = category.id
  categorySearch.value = category.name
  showCategoryDropdown.value = false
}

// ========== 平台：只能下拉选择 ==========
const filterPlatforms = () => {
  const searchTerm = platformSearch.value.toLowerCase()
  filteredPlatforms.value = platforms.value.filter(p =>
    p.name.toLowerCase().includes(searchTerm)
  )
}

const onPlatformInput = () => {
  filterPlatforms()
  showPlatformDropdown.value = true
  const matched = platforms.value.find(p => p.name === platformSearch.value)
  if (matched) {
    form.platformId = matched.platform_id
  } else {
    form.platformId = null
  }
}

const onPlatformBlur = () => {
  setTimeout(() => {
    const matched = platforms.value.find(p => p.name === platformSearch.value)
    if (!matched) {
      platformSearch.value = ''
      form.platformId = null
    }
    showPlatformDropdown.value = false
  }, 200)
}

const selectPlatform = (platform: PlatformOption) => {
  form.platformId = platform.platform_id
  platformSearch.value = platform.name
  showPlatformDropdown.value = false
}

// ========== 国家：只能下拉选择 ==========
const filterCountries = () => {
  const searchTerm = countrySearch.value.toLowerCase()
  filteredCountries.value = countries.filter(country => 
    country.toLowerCase().includes(searchTerm)
  )
}

const onCountryInput = () => {
  filterCountries()
  showCountryDropdown.value = true
  const matched = countries.find(c => c === countrySearch.value)
  if (matched) {
    form.country = matched
  } else {
    form.country = ''
  }
}

const onCountryBlur = () => {
  setTimeout(() => {
    const matched = countries.find(c => c === countrySearch.value)
    if (!matched) {
      countrySearch.value = ''
      form.country = ''
    }
    showCountryDropdown.value = false
  }, 200)
}

const selectCountry = (country: string) => {
  form.country = country
  countrySearch.value = country
  showCountryDropdown.value = false
}

// ========== 视频链接验证 ==========
const urlRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/

const validateVideoUrl = () => {
  if (!form.videoUrl || form.videoUrl.trim() === '') {
    videoUrlError.value = ''
    return true
  }
  if (!urlRegex.test(form.videoUrl)) {
    videoUrlError.value = '请输入有效的URL地址（以 http:// 或 https:// 开头）'
    return false
  }
  videoUrlError.value = ''
  return true
}

const resetForm = () => {
  Object.assign(form, {
    title: '',
    director: '',
    year: String(new Date().getFullYear()),
    duration: '',
    tvChannelId: null,
    categoryId: null,
    platformId: null,
    videoUrl: '',
    country: '',
    description: '',
    tags: [],
    submitterName: '',
    // 保留已验证用户的邮箱
    submitterEmail: userStore.user?.email && userStore.user?.is_email_verified ? userStore.user.email : '',
    submitReason: ''
  })
  tagInput.value = ''
  countrySearch.value = ''
  tvChannelSearch.value = ''
  categorySearch.value = ''
  platformSearch.value = ''
  filteredCountries.value = countries
  filteredTvChannels.value = tvChannels.value
  filteredCategories.value = categories.value
  filteredPlatforms.value = platforms.value
}

const handleSubmit = async () => {
  if (isSubmitting.value) return

  // 验证必填字段
  if (!form.tvChannelId) {
    if (window.globalModal && window.globalModal.showMessage) {
      window.globalModal.showMessage('验证失败', '请选择电视频道', 'error')
    }
    return
  }

  // 验证视频链接格式
  if (!validateVideoUrl()) {
    if (window.globalModal && window.globalModal.showMessage) {
      window.globalModal.showMessage('验证失败', videoUrlError.value, 'error')
    }
    return
  }

  isSubmitting.value = true

  try {
    // 准备提交数据 - 字段名与后端 CreateSubmissionRequest 一致
    const submissionData: Record<string, any> = {
      title: form.title,
      director: form.director,
      year: parseInt(form.year),
      country: form.country || undefined,
      description: form.description,
      tv_channel_id: form.tvChannelId,
      submitter_name: form.submitterName || undefined,
      submitter_email: form.submitterEmail,
      submit_reason: form.submitReason || undefined,
      tags: form.tags
    }

    if (form.duration) {
      submissionData.duration = parseInt(form.duration)
    }
    if (form.platformId) {
      submissionData.platform_id = form.platformId
    }
    if (form.videoUrl) {
      submissionData.video_url = form.videoUrl
    }

    // 调用API提交数据
    await submissionAPI.createSubmission(submissionData as {
      title: string
      director: string
      year: number
      duration?: number
      country?: string
      description: string
      tv_channel_id: number
      platform_id?: number
      video_url?: string
      submitter_name?: string
      submitter_email: string
      submit_reason?: string
      tags?: string[]
    })

    // 显示成功提示
    showSuccess.value = true

    // 重置表单（保留邮箱）
    resetForm()
  } catch (error) {
    debugError('提交失败:', error)
    // 显示错误提示给用户
    if (window.globalModal && window.globalModal.showMessage) {
      window.globalModal.showMessage('提交失败', error instanceof Error ? error.message : '提交失败，请重试', 'error')
    }
  } finally {
    isSubmitting.value = false
  }
}

const closeSuccess = () => {
  showSuccess.value = false
  // 同时关闭主模态框
  emit('close')
}
</script>

<style scoped>
.documentary-submission {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(220, 53, 69, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-content);
}

.close-btn:hover {
  background: rgba(220, 53, 69, 1);
  transform: scale(1.1) rotate(90deg);
}

.submission-header {
  text-align: center;
  margin-bottom: 40px;
}

.submission-header h2 {
  font-size: 2.5em;
  color: var(--dark-text);
  margin-bottom: 10px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.submission-header p {
  font-size: 1.2em;
  color: var(--light-text);
}

.submission-form {
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 40px;
}

.form-section h3 {
  font-size: 1.4em;
  color: var(--dark-text);
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--primary-color);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--dark-text);
}

.form-group label::after {
  content: attr(data-required);
  color: var(--danger-color);
  margin-left: 4px;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(102, 126, 234, 0.2);
  border-radius: 12px;
  font-size: 1em;
  background: rgba(255, 255, 255, 0.8);
  color: var(--dark-text);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  background: rgba(255, 255, 255, 0.95);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.custom-select-wrapper {
  position: relative;
}

.custom-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid rgba(102, 126, 234, 0.2);
  border-top: none;
  border-radius: 0 0 12px 12px;
  max-height: 200px;
  overflow-y: auto;
  z-index: var(--z-content);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.custom-select-option {
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
}

.custom-select-option:last-child {
  border-bottom: none;
}

.custom-select-option:hover {
  background: rgba(102, 126, 234, 0.1);
}

.custom-select-option:first-child {
  border-radius: 0 0 0 12px;
}

.custom-select-option:last-child {
  border-radius: 0 0 12px 0;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--gradient-primary);
  color: white;
  border-radius: 20px;
  font-size: 0.9em;
  font-weight: 500;
}

.tag-remove {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1.2em;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.tag-remove:hover {
  opacity: 1;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(102, 126, 234, 0.2);
}

.btn {
  padding: 12px 30px;
  border: none;
  border-radius: 25px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.btn-primary {
  background: var(--gradient-primary);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: transparent;
  color: var(--dark-text);
  border: 2px solid rgba(102, 126, 234, 0.3);
}

.btn-secondary:hover {
  background: rgba(102, 126, 234, 0.1);
  border-color: var(--primary-color);
}

.success-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.success-modal .modal-content {
  background: white;
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  z-index: var(--z-modal);
  position: relative;
  box-shadow: 0 20px 60px rgba(44, 62, 80, 0.4);
  border: 1px solid rgba(139, 115, 85, 0.3);
}

.success-icon {
  font-size: 4em;
  color: #4CAF50;
  margin-bottom: 20px;
}

.success-modal h3 {
  font-size: 1.8em;
  color: var(--dark-text);
  margin-bottom: 15px;
}

.success-modal p {
  color: var(--light-text);
  margin-bottom: 25px;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .documentary-submission {
    padding: 15px;
  }
  
  .submission-form {
    padding: 20px;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
}

/* 视频链接验证错误样式 */
.input-error {
  border-color: #f56c6c !important;
  box-shadow: 0 0 0 3px rgba(245, 108, 108, 0.1) !important;
}

.field-error {
  color: #f56c6c;
  font-size: 0.85em;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-error::before {
  content: '⚠';
  font-size: 1.1em;
}
</style>